import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithRetry, ENDPOINT_TAGS, generateEndpoint } from '../../../core/store/rootApi';
import {
  ArticleFindRequest,
  ArticleGetInCategoryRequest,
  ArticleGetResponse,
  ArticleParams,
  ArticleSearchResultsResponse,
} from '../model/articleModel';

export const articleApi = createApi({
  reducerPath: 'articleApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [
    ENDPOINT_TAGS.ARTICLE,
    ENDPOINT_TAGS.ARTICLES,
    ENDPOINT_TAGS.ARTICLES_IN_CATEGORY,
    ENDPOINT_TAGS.ARTICLES_MOST_SEARCHED,
    ENDPOINT_TAGS.ARTICLES_SEARCH,
  ],
  endpoints: (builder) => ({
    getArticles: builder.query<ArticleGetResponse[], ArticleParams>({
      query: (params) => ({
        url: '/api/articles',
        params: params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: ENDPOINT_TAGS.ARTICLES as const, id })),
              { type: ENDPOINT_TAGS.ARTICLES, id: 'LIST' },
            ]
          : [{ type: ENDPOINT_TAGS.ARTICLES, id: 'LIST' }],
    }),
    getArticle: builder.query<ArticleGetResponse, ArticleParams>({
      query: ({ id }) => generateEndpoint('/api/articles/:id', { id: id! }),
      providesTags: (result, error, args) => [{ type: ENDPOINT_TAGS.ARTICLE, id: args.id }],
    }),
    getArticlesSearch: builder.query<ArticleSearchResultsResponse[], ArticleFindRequest>({
      query: (params) => ({
        url: '/api/articles/search/',
        params: params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: ENDPOINT_TAGS.ARTICLES_SEARCH as const, id })),
              { type: ENDPOINT_TAGS.ARTICLES_SEARCH, id: 'LIST' },
            ]
          : [{ type: ENDPOINT_TAGS.ARTICLES_SEARCH, id: 'LIST' }],
    }),
    getMostSearchedArticles: builder.query<ArticleSearchResultsResponse[], void>({
      query: () => '/api/articles/most-searched',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: ENDPOINT_TAGS.ARTICLES_MOST_SEARCHED as const, id })),
              { type: ENDPOINT_TAGS.ARTICLES_MOST_SEARCHED, id: 'LIST' },
            ]
          : [{ type: ENDPOINT_TAGS.ARTICLES_MOST_SEARCHED, id: 'LIST' }],
    }),
    getArticlesInCategory: builder.query<ArticleGetResponse[], ArticleGetInCategoryRequest>({
      query: ({ id }) => generateEndpoint('/api/categories/:id/articles', { id: id! }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: ENDPOINT_TAGS.ARTICLES_IN_CATEGORY as const, id })),
              { type: ENDPOINT_TAGS.ARTICLES_IN_CATEGORY, id: 'LIST' },
            ]
          : [{ type: ENDPOINT_TAGS.ARTICLES_IN_CATEGORY, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetArticleQuery,
  useGetArticlesInCategoryQuery,
  useGetArticlesQuery,
  useGetArticlesSearchQuery,
  useGetMostSearchedArticlesQuery,
  useLazyGetArticlesInCategoryQuery,
} = articleApi;
